import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { db } from '../../firebase/config';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import AbhyaasLogo from '../../assets/images/AbhyaasLogoSquareTransparent.svg';
import './StallOwnerRegPage.css';

const StallOwnerRegPage = () => {
    const [formData, setFormData] = useState({
        stallOwnerName: '',
        stallName: '',
        phoneNumber: '',
        email: ''
    });
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        // Validation
        if (!formData.stallOwnerName || !formData.stallName || !formData.phoneNumber) {
            setError('Please fill in all required fields');
            setLoading(false);
            return;
        }

        if (formData.phoneNumber.length !== 10 || !/^\d+$/.test(formData.phoneNumber)) {
            setError('Please enter a valid 10-digit phone number');
            setLoading(false);
            return;
        }

        try {
            const docRef = await addDoc(collection(db, 'stall_owner_registrations'), {
                stallOwnerName: formData.stallOwnerName.trim(),
                stallName: formData.stallName.trim(),
                phoneNumber: formData.phoneNumber.trim(),
                email: formData.email.trim(),
                registeredAt: serverTimestamp(),
                createdAt: new Date().toISOString()
            });

            console.log('Registration successful:', docRef.id);
            setSubmitted(true);
            setLoading(false);
        } catch (err) {
            console.error('Error registering:', err);
            console.error('Error details:', err.message, err.code);
            setError(`Registration failed: ${err.message || 'Please try again or contact admin.'}`);
            setLoading(false);
        }
    };

    if (submitted) {
        return (
            <div className="stall-owner-page">
                <motion.div
                    className="success-container"
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: "spring", duration: 0.8 }}
                >
                    <div className="success-icon">✓</div>
                    <h1>Registration Successful!</h1>
                    <p className="success-message">
                        Thank you for registering! You are now eligible for the lucky draw.
                        Winners will be announced soon!
                    </p>
                    <motion.div
                        className="success-confetti"
                        animate={{
                            opacity: [0, 1, 1, 0],
                            scale: [0.5, 1.2, 1.2, 0.5]
                        }}
                        transition={{ duration: 2, repeat: Infinity }}
                    >
                        🎉
                    </motion.div>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="stall-owner-page">
            <motion.div
                className="stall-owner-container"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <div className="header-section">
                    <img src={AbhyaasLogo} alt="Abhyaas" className="logo" />
                    <h1>Stall Owner Lucky Draw</h1>
                </div>

                <motion.div
                    className="thank-you-section"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 }}
                >
                    <div className="thank-you-icon">🎪</div>
                    <h2>Thank You for Your Participation!</h2>
                    <p className="thank-you-text">
                        Your contribution with your lovely stall made the Little Elly Kudlu
                        Deepawali Carnival a grand success!
                    </p>
                    <p className="gift-text">
                        <span className="gift-icon">🎁</span>
                        As a token of our appreciation, you are eligible for a lucky draw
                        to win a <strong>₹1,000 Amazon Gift Voucher!</strong>
                    </p>
                    <p className="announcement-text">
                        Winners will be announced soon. Good luck!
                    </p>
                    <div className="important-notice">
                        <span className="notice-icon">⚠️</span>
                        <strong>Important:</strong> Please register only ONE person per stall.
                        One entry per stall only.
                    </div>
                </motion.div>

                <motion.form
                    className="registration-form"
                    onSubmit={handleSubmit}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                >
                    <h3>Register for Lucky Draw</h3>

                    <div className="form-group">
                        <label>Stall Owner Name *</label>
                        <input
                            type="text"
                            name="stallOwnerName"
                            value={formData.stallOwnerName}
                            onChange={handleChange}
                            placeholder="Enter your name"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Stall Name *</label>
                        <input
                            type="text"
                            name="stallName"
                            value={formData.stallName}
                            onChange={handleChange}
                            placeholder="Enter your stall name"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Phone Number *</label>
                        <input
                            type="tel"
                            name="phoneNumber"
                            value={formData.phoneNumber}
                            onChange={handleChange}
                            placeholder="10-digit mobile number"
                            maxLength="10"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Email (Optional)</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="your.email@example.com"
                        />
                    </div>

                    <AnimatePresence>
                        {error && (
                            <motion.div
                                className="error-message"
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                            >
                                {error}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <motion.button
                        type="submit"
                        className="submit-button"
                        disabled={loading}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                    >
                        {loading ? 'Registering...' : 'Register Now'}
                    </motion.button>
                </motion.form>

                <div className="footer-note">
                    <p>🪔 Thank you for making our Deepawali celebration memorable! 🪔</p>
                </div>
            </motion.div>
        </div>
    );
};

export default StallOwnerRegPage;
